import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Car, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get('type') || 'rider';
  const referralCode = searchParams.get('ref') || '';
  const [userType, setUserType] = useState(defaultType);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    referral_code: referralCode,
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        user_type: userType,
        referral_code: formData.referral_code || undefined,
      });
      
      toast.success('Account created successfully!');
      
      if (userType === 'driver') {
        navigate('/driver/onboarding');
      } else {
        navigate('/rider');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1765208269671-4cc3bd040826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMGNpdHklMjBzdHJlZXQlMjBuaWdodCUyMHJlZCUyMHRhaWxsaWdodHMlMjBtb3Rpb24lMjBibHVyfGVufDB8fHx8MTc2ODUwOTg5OXww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-slate-900/90"></div>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
            <Car className="w-7 h-7 text-white" />
          </div>
          <span className="text-3xl font-display font-bold text-white tracking-tight">REDDI</span>
        </Link>

        {/* Register Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-2xl font-display font-bold text-white uppercase tracking-tight text-center mb-6">
            Create Account
          </h1>

          <Tabs value={userType} onValueChange={setUserType} className="mb-6">
            <TabsList className="grid w-full grid-cols-2 bg-white/10">
              <TabsTrigger 
                value="rider" 
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
                data-testid="rider-register-tab"
              >
                Rider
              </TabsTrigger>
              <TabsTrigger 
                value="driver"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
                data-testid="driver-register-tab"
              >
                Driver
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Referral Banner */}
          {referralCode && userType === 'driver' && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg" data-testid="referral-banner">
              <div className="flex items-center gap-2 text-green-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                <span className="font-medium">Referral Bonus Active!</span>
              </div>
              <p className="text-green-300/80 text-sm mt-1">
                You've been referred by a Reddi driver. Complete your registration and 10 rides to unlock bonus earnings!
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-white">Full Name</Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={formData.phone}
                onChange={handleChange}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider transition-all duration-300"
              data-testid="register-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                `Sign up as ${userType === 'driver' ? 'Driver' : 'Rider'}`
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-400">
            Already have an account?{' '}
            <Link 
              to={`/login?type=${userType}`} 
              className="text-red-500 hover:text-red-400 font-semibold transition-colors"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
